import React, { useContext } from 'react'
import { useSelector } from 'react-redux'
import { CampaignContext } from '../models'
import { MetricFilter } from '../components/ReportingFilter/PopoverFilters/MetricFilter'
import { NameFilter } from '../components/ReportingFilter/PopoverFilters/NameFilter'
import { StatusFilter } from '../components/ReportingFilter/PopoverFilters/StatusFilter'
import { ChannelFilter } from '../components/ReportingFilter/PopoverFilters/ChannelFilter'
import { selectReportingData } from '../store/reportingData'
import { EDatasetKey } from '@unfoldrtech/portal-mic/dist/src/openapi/core'
import { ECampaignStatus } from '@unfoldrtech/portal-mic'

const CAMPAIGN_DATASET_KEYS = [
  EDatasetKey.CampaignGeneric,
  EDatasetKey.CampaignSp,
  EDatasetKey.CampaignDa,
  EDatasetKey.CampaignDooh,
]

const DEFAULT_CAMPAIGN_STATUSES = [
  ECampaignStatus.Active,
  ECampaignStatus.Inactive,
  ECampaignStatus.Pending,
  ECampaignStatus.Accepted,
]

export const getDefaultStatusesForDataset = (
  datasetKey?: EDatasetKey
): ECampaignStatus[] | undefined => {
  if (datasetKey && CAMPAIGN_DATASET_KEYS.includes(datasetKey)) {
    return DEFAULT_CAMPAIGN_STATUSES
  }
  return undefined
}

const getStatusSelectionMode = (datasetKey?: EDatasetKey) => {
  if (datasetKey && CAMPAIGN_DATASET_KEYS.includes(datasetKey)) {
    return 'multiple'
  }
  return 'single'
}

export const useGetReportingFilterOptions = () => {
  const datasetKey = useSelector(selectReportingData)
  const campaign = useContext(CampaignContext)

  const campaignId = campaign?.id

  let statuses: Array<ECampaignStatus> = [
    ECampaignStatus.Active,
    ECampaignStatus.Inactive,
    ECampaignStatus.Disabled,
  ]
  const isReportingCampaign = !!(
    datasetKey === EDatasetKey.CampaignGeneric ||
    datasetKey === EDatasetKey.CampaignSp ||
    datasetKey === EDatasetKey.CampaignDa ||
    datasetKey === EDatasetKey.CampaignDooh
  )
  const isReportingCreative = !!(
    datasetKey === EDatasetKey.CreativeDa ||
    datasetKey === EDatasetKey.CreativeDaAdgroup ||
    datasetKey === EDatasetKey.CreativeDooh ||
    datasetKey === EDatasetKey.CreativeDoohAdgroup
  )

  if (isReportingCampaign) {
    statuses = [
      ECampaignStatus.Active,
      ECampaignStatus.Inactive,
      ECampaignStatus.Pending,
      ECampaignStatus.Accepted,
      ECampaignStatus.Disabled,
      ECampaignStatus.Rejected,
      ECampaignStatus.Completed,
    ]
  }
  if (isReportingCreative) {
    statuses = [
      ECampaignStatus.Active,
      ECampaignStatus.Inactive,
      ECampaignStatus.Disabled,
      ECampaignStatus.Pending,
      ECampaignStatus.Rejected,
    ]
  }

  let options = [
    {
      key: 'metric',
      name: 'reporting.filter.filter.by.metric',
      popoverContent: <MetricFilter />,
    },
    {
      key: 'name',
      name: 'reporting.filter.filter.by.name',
      popoverContent: <NameFilter />,
    },
    {
      key: 'status',
      name: 'reporting.filter.filter.by.status',
      popoverContent: (
        <StatusFilter
          statuses={statuses}
          selectionMode={getStatusSelectionMode(datasetKey)}
        />
      ),
    },
    {
      key: 'channel',
      name: 'reporting.filter.filter.by.channel',
      popoverContent: <ChannelFilter />,
    },
  ]

  // No channel as filter for the moment
  options = options.filter((option) => option.key !== 'channel')

  const isReportingTotalsPage =
    !campaignId &&
    (datasetKey === EDatasetKey.ProductSp ||
      datasetKey === EDatasetKey.ProductDa ||
      datasetKey === EDatasetKey.KeywordSp ||
      datasetKey === EDatasetKey.KeywordDa ||
      datasetKey === EDatasetKey.CategorySp ||
      datasetKey === EDatasetKey.CategoryDa)

  if (isReportingTotalsPage) {
    options = options.filter((option) => option.key !== 'status')
  }

  return options
}
